import { App } from '@capacitor/app';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AlertController, NavController } from '@ionic/angular';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { Device } from '@capacitor/device';
import { Browser } from '@capacitor/browser';
import { IS_NEW_UI_FLOW, SERVER_URL } from '../../../environments/environment';
import { ToastService } from '../services/toast.service';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { TranslateService } from '@ngx-translate/core';
import { Injector } from '@angular/core';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private navCtrl = inject(NavController);
  private alertCtrl = inject(AlertController);
  private toastService = inject(ToastService);

  constructor(private injector: Injector) { } // << only injector, not services

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return from(this.getHeaders(request)).pipe(
      switchMap((headers) => {
        request = request.clone({
          headers,
          url: request.url.replace('api_endpoint', SERVER_URL),
        });
        return next.handle(request);
      }),
      catchError((error: HttpErrorResponse) => {
        let errorMessage = '';

        // Check if navigation should be skipped for this request
        const skipNavigation = request.headers.get('X-Skip-Navigation') === 'true';

        if (error.status === 400) {
          errorMessage = this.presentErrorMessage(error);
        } else if (error.status === 403 || error.status === 401) {
          // Check if this is an SSO-related request
          const isSsoRequest = request.url.includes('sso') || request.url.includes('callback');

          if (!isSsoRequest) {
            // Clear auth data on 401/403 errors (but not for SSO requests)
            localStorage.removeItem('token');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('cognitoUsername');

            this.navCtrl.navigateRoot(`${IS_NEW_UI_FLOW ? '/auth' : '/login'}`);
            this.toastService.presentDangerToast('Session expired. Please login again.');
          } else {
            //console.log('SSO request failed with 401/403, not clearing auth data');
            this.toastService.presentDangerToast('SSO authentication failed. Please try again.');
          }
        } else if (error.status === 404) {
          if (!skipNavigation) {
            this.navCtrl.navigateRoot('');
          }
          this.toastService.presentDangerToast(error?.error?.message);
          return throwError(() => error);
        } else if (error.status === 406) {
          if (Capacitor.isNativePlatform()) this.requireUpdate();
        } else if (error.status === 422 && error.error?.code === 'CAPTCHA_VALIDATION_FAILED') {
          this.toastService.presentDangerToast(error?.error?.message);
          return throwError(() => error);
        } else if (error.status > 499) {
          const translateService = this.injector.get(TranslateService);
          this.toastService.presentDangerToast(
            translateService.instant(
              'Services are temporarily unavailable. If the issue presists please reach out via feedback@ionsport.com.'
            ));
        } else if (error.status === 413) {
          this.toastService.presentDangerToast(error.error);
        } else if (error.status === 0) {
          this.toastService.presentDangerToast(
            'Some thing went wrong, please try again later'
          );
        } else {
          errorMessage = this.presentErrorMessage(error);
        }

        return throwError(() => errorMessage);
      }),
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const authHeader = event.headers.get('Authorization');
          if (authHeader) {
            localStorage.setItem('token', authHeader);
            localStorage.setItem('accessToken', authHeader.split(' ')[1]);
          }

          const refreshTokenHeader = event.headers.get('Refresh-Token');
          if (refreshTokenHeader) {
            localStorage.setItem('refreshToken', refreshTokenHeader);
          }

          const accessTokenHeader = event.headers.get('access-token');
          if (accessTokenHeader) {
            localStorage.setItem('accessToken', accessTokenHeader);
          }

          const cognitoUsernameHeader = event.headers.get('cognito-username');
          if (cognitoUsernameHeader) {
            localStorage.setItem('cognitoUsername', cognitoUsernameHeader);
          }
        }
      })
    );
  }

  private async getHeaders(request: HttpRequest<any>): Promise<HttpHeaders> {
    let headers: HttpHeaders = new HttpHeaders();
    headers = request.headers;

    const accessToken = localStorage.getItem('accessToken');
    const cognitoUsername = localStorage.getItem('cognitoUsername');

    if (accessToken && cognitoUsername) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
      headers = headers.set('cognito-username', cognitoUsername);
    } else {
      const token = localStorage.getItem('token');
      if (token) {
        headers = headers.set('Authorization', token);
      }
    }

    const refreshToken = localStorage.getItem('refreshToken');

    let deviceId = (await Preferences.get({ key: 'deviceId' }))?.value;
    if (deviceId) {
      headers = headers.set('device-id', deviceId);
    }

    if (refreshToken) {
      headers = headers.set('Refresh-Token', refreshToken);
    } else {
      // SET DEVICE INFORMATION IN HEADER
      // if (Capacitor.isNativePlatform()) {
      //     const deviceId = (await Device?.getId())?.identifier;
      //     const deviceInfo = await Device?.getInfo();
      //     const batteryInfo = await Device?.getBatteryInfo();
      //     if (deviceId) {
      //         headers = headers.set('device-id', deviceId);
      //     }
      //     if (deviceInfo) {
      //         headers = headers.set('device-info', JSON.stringify(deviceInfo));
      //     }
      //     if (batteryInfo) {
      //         headers = headers.set('battery-info', JSON.stringify(batteryInfo));
      //     }
      // }
    }

    const appVersion = localStorage.getItem('appVersion');
    if (appVersion) {
      headers = headers.set('Version', appVersion);
    }

    if (
      request.url.startsWith('http://blog.ioncourt.com/wp-json/wp/v2/posts')
    ) {
      headers = new HttpHeaders();
      headers.append('Access-Control-Allow-Origin', '*');
      headers.append(
        'Access-Control-Allow-Methods',
        'POST, GET, OPTIONS, DELETE'
      );
      headers.append('Content-Type', 'application/json');
    }

    return headers;
  }

  private presentErrorMessage(e: HttpErrorResponse): any {
    if (e.error) {
      const errorMessage =
        e.error instanceof ErrorEvent
          ? `Error: ${e.error.message}` // client-side error
          : e.error; // server-side error
      // this.toastService.presentDangerToast(errorMessage.message ? errorMessage.message : errorMessage);
      return errorMessage;
    }
  }

  async requireUpdate() {
    const alert = await this.alertCtrl.create({
      header: 'App updated required',
      message: 'Please, update the app to continue',
      buttons: [
        {
          text: 'Update',
          handler: async () => {
            let version = '';
            if (Capacitor.isNativePlatform()) {
              const appInfo = await App.getInfo();
              version = appInfo.version;
            } else {
              version = 'browser';
            }

            localStorage.setItem('appVersion', version);
            localStorage.removeItem('token');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('cognitoUsername');
            this.navCtrl.navigateRoot('login');

            await Browser.open({ url: await this.getUrl() });
          },
        },
      ],
    });

    await alert.present();
  }

  async getUrl() {
    const { platform } = await Device.getInfo();
    return platform === 'ios'
      ? 'https://apps.apple.com/us/app/ioncourt/id1489506140'
      : 'https://play.google.com/store/apps/details?id=com.ioncourt.tracker';
  }
}
